import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";

import {
  clearSession,
  ensureValidTokens,
  forceRefreshTokens,
  getStoredTokens,
  getUserProfile,
  handleCallback,
  initiateLogin,
  isAuthenticated as checkIsAuthenticated,
  logout as keycloakLogout,
  resolvePermissionRoles,
  type UserProfile,
} from "~/features/auth/services/keycloakService";
import { type OrganizationInfo } from "~/features/auth/types/keycloak";
import {
  clearStoredSelectedTenantId,
  resolveTenantSelection,
  storeSelectedTenantId,
  switchUserTenant,
} from "~/features/auth/utils/authTenant";
import { AUTH_EVENTS } from "~/lib/customEvents";

type TokenStatus = "valid" | "expiring" | "expired" | "unknown";

interface AuthSessionState {
  user: UserProfile | null;
  accessToken: string | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  tokenStatus: TokenStatus;
  selectedTenantId: string | null;
  availableOrganizations: OrganizationInfo[];
  /** Post-login redirect path resolved from the pre-auth URL. */
  postLoginRedirect: string | null;
  /** Error message from the most recent auth attempt, if any. */
  authError: string | null;
}

interface AuthSessionResult extends AuthSessionState {
  tenantId: string | null;
  loginWithKeycloak: () => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => void;
  /** Force-refresh the access token to pick up Keycloak role/group changes. */
  refreshRoles: () => Promise<void>;
}

const TOKEN_REFRESH_INTERVAL_MS = 60_000;
const TOKEN_EXPIRING_THRESHOLD_MINUTES = 5;

const getTokenStatus = (expiresAt: number): TokenStatus => {
  const minutesUntilExpiry = (expiresAt - Date.now()) / (1000 * 60);

  if (minutesUntilExpiry > TOKEN_EXPIRING_THRESHOLD_MINUTES) {
    return "valid";
  }

  if (minutesUntilExpiry > 0) {
    return "expiring";
  }

  return "expired";
};

const OAUTH_PARAMS = ["code", "state", "error", "error_description", "session_state"];

const clearAuthSearchParams = (): void => {
  const url = new URL(window.location.href);

  OAUTH_PARAMS.forEach((p) => url.searchParams.delete(p));

  const cleanUrl = url.pathname + url.search + url.hash;

  window.history.replaceState({}, document.title, cleanUrl);
  // Notify React Router about the URL change so its internal state stays in sync.
  window.dispatchEvent(new PopStateEvent("popstate"));
};

export const useAuthSession = (): AuthSessionResult => {
  const didInit = useRef(false);

  const [state, setState] = useState<AuthSessionState>({
    user: null,
    accessToken: null,
    isLoading: true,
    isLoggingOut: false,
    tokenStatus: "unknown",
    selectedTenantId: null,
    availableOrganizations: [],
    postLoginRedirect: null,
    authError: null,
  });

  const resetSessionState = useCallback(() => {
    clearStoredSelectedTenantId();

    setState((current) => ({
      ...current,
      user: null,
      accessToken: null,
      selectedTenantId: null,
      availableOrganizations: [],
      tokenStatus: "expired",
      isLoggingOut: false,
    }));
  }, []);

  const initializeAuth = useEffectEvent(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const stateParam = urlParams.get("state");
    const error = urlParams.get("error");

    if (error) {
      const errorDescription = urlParams.get("error_description") || error;
      console.error("[Auth] Keycloak returned error:", error, errorDescription);

      clearSession();

      clearAuthSearchParams();
      setState((current) => ({
        ...current,
        isLoading: false,
        tokenStatus: "unknown",
        authError: `Keycloak error: ${errorDescription}`,
      }));

      return;
    }

    if (code && stateParam) {
      try {
        const result = await handleCallback(code, stateParam);
        const resolved = resolveTenantSelection(result.userProfile);

        // Single atomic setState so isAuthenticated and isLoading flip together,
        // preventing any intermediate render where isLoading=false + user=null
        // that would trigger the callback component's redirect to /login.
        setState((current) => ({
          ...current,
          user: resolved.user,
          accessToken: result.tokens.access_token,
          tokenStatus: "valid",
          selectedTenantId: resolved.selectedTenantId,
          availableOrganizations: resolved.availableOrganizations,
          postLoginRedirect: result.redirectTo,
          isLoading: false,
          authError: null,
        }));
      } catch (err) {
        console.error("[Auth] Callback failed:", err);

        clearSession();
        clearStoredSelectedTenantId();

        setState((current) => ({
          ...current,
          user: null,
          accessToken: null,
          selectedTenantId: null,
          availableOrganizations: [],
          tokenStatus: "expired",
          isLoggingOut: false,
          isLoading: false,
          authError: err instanceof Error ? err.message : "Authentication failed",
        }));
      } finally {
        clearAuthSearchParams();
      }

      return;
    }

    if (checkIsAuthenticated()) {
      try {
        const tokens = getStoredTokens();

        if (tokens) {
          const userProfile = await getUserProfile();
          const resolved = resolveTenantSelection(userProfile);

          // Single setState for the entire session restore — combining user,
          // token, tenant, and loading state prevents intermediate renders where
          // tenantId is set but isLoading is still true (which would briefly
          // enable queries before the spinner has cleared).
          setState((current) => ({
            ...current,
            user: resolved.user,
            accessToken: tokens.access_token,
            tokenStatus: getTokenStatus(tokens.expires_at),
            selectedTenantId: resolved.selectedTenantId,
            availableOrganizations: resolved.availableOrganizations,
            isLoading: false,
          }));

          return;
        }
      } catch (err) {
        console.warn("[Auth] Session restore failed, clearing session:", err);
        clearSession();
        resetSessionState();
      }
    }

    setState((current) => ({ ...current, isLoading: false }));
  });

  const refreshSession = useEffectEvent(async () => {
    try {
      const tokens = await ensureValidTokens();

      if (!tokens) {
        // Only reset if the user was previously authenticated — avoids
        // clearing state during an initial load before auth completes.
        if (state.user) {
          console.warn("[Auth] Session expired — tokens could not be refreshed");

          clearStoredSelectedTenantId();
          setState((current) => ({
            ...current,
            user: null,
            accessToken: null,
            selectedTenantId: null,
            availableOrganizations: [],
            tokenStatus: "expired",
            isLoggingOut: false,
            authError: "Your session has expired. Please sign in again.",
          }));
        }

        return;
      }

      const refreshedRoles = await resolvePermissionRoles(tokens.access_token, tokens.id_token);

      setState((current) => ({
        ...current,
        accessToken: tokens.access_token,
        tokenStatus: getTokenStatus(tokens.expires_at),
        // Sync roles from the refreshed JWT so RBAC reflects Keycloak changes
        // (e.g. group additions) without requiring a full re-login.
        user: current.user ? { ...current.user, roles: refreshedRoles } : current.user,
      }));
    } catch (err) {
      // Keep the existing session state and let the next interval retry.
      console.warn("[Auth] Token refresh failed, will retry on next interval:", err);
    }
  });

  useEffect(() => {
    // Prevent double initialization in React StrictMode (dev) where effects
    // run twice. The first call clears OAuth state from localStorage; a second
    // call would fail with "Invalid state parameter".
    if (didInit.current) {
      return;
    }

    didInit.current = true;

    void initializeAuth();

    const intervalId = window.setInterval(() => {
      void refreshSession();
    }, TOKEN_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  const loginWithKeycloak = useCallback(async (returnTo?: string) => {
    setState((current) => ({ ...current, authError: null }));

    await initiateLogin(returnTo);
  }, []);

  const logout = useCallback(async () => {
    setState((current) => {
      if (current.isLoggingOut) {
        return current;
      }

      return {
        ...current,
        user: null,
        accessToken: null,
        selectedTenantId: null,
        availableOrganizations: [],
        isLoggingOut: true,
      };
    });

    clearStoredSelectedTenantId();
    window.dispatchEvent(new Event(AUTH_EVENTS.sessionCleared));
    keycloakLogout();
  }, []);

  const switchTenant = useCallback((tenantId: string) => {
    window.dispatchEvent(new Event(AUTH_EVENTS.sessionCleared));

    setState((current) => {
      if (!current.user) {
        return current;
      }

      const nextUser = switchUserTenant(current.user, current.availableOrganizations, tenantId);
      if (!nextUser) {
        return current;
      }

      storeSelectedTenantId(tenantId);

      return {
        ...current,
        user: nextUser,
        selectedTenantId: tenantId,
      };
    });
  }, []);

  const refreshRoles = useCallback(async () => {
    const tokens = await forceRefreshTokens();

    if (!tokens) {
      return;
    }

    const refreshedRoles = await resolvePermissionRoles(tokens.access_token, tokens.id_token);

    setState((current) => ({
      ...current,
      accessToken: tokens.access_token,
      tokenStatus: getTokenStatus(tokens.expires_at),
      user: current.user ? { ...current.user, roles: refreshedRoles } : current.user,
    }));
  }, []);

  const tenantId = state.selectedTenantId || state.user?.tenantId || null;

  return {
    ...state,
    tenantId,
    loginWithKeycloak,
    logout,
    switchTenant,
    refreshRoles,
  };
};
